<!--
  canonical product editor VariantMenu -- popover for editing a single
  color's variant routing from the ColorFilmstrip.

  Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
        02-product-editor.md (Day 3 -- Filmstrip + VariantMenu)

  Slice 0 (editable-variant-cards, 2026-07-15) -- OUTSIDE-CLICK DISMISS.
    The menu is a Vuetify v-menu (prior art: sections/LDPipPopover.vue),
    which dismisses on outside-click AND Escape by DEFAULT, teleports to
    the overlay layer (escaping any overflow:hidden ancestor), and
    auto-flips near the viewport edge. The activator (the color badge)
    lives in the parent (ColorFilmstrip) and is passed in via the
    #activator slot. Open state is a two-way `open` prop (v-model:open).
    `:close-on-content-click="false"` keeps the menu open when an action
    button is clicked so its handler fires; each action then closes
    explicitly via the co-emitted `close`.

  Slice 4 (editable-variant-cards, 2026-07-15) -- PER-VARIANT ASSIGNMENT.
    The menu previously offered a fixed "Set as Light" / "Set as Dark" /
    "Reset to auto" trio. It now ENUMERATES every variant on the placement
    (one "Assign to <variant>" option each) + "Reset to auto". Choosing a
    variant assigns THIS color to it with steal-silently semantics (the
    parent decides the write per target kind -- catch-all writes
    color_overrides, specific reuses set_colors; see ColorFilmstrip). The
    option for the variant that already OWNS this color is marked current
    and disabled (nothing to do). When the color currently belongs to
    ANOTHER variant, a steal TOOLTIP on the color pip (the activator, in
    the parent) warns "Currently on <other> -- assigning here moves it";
    the menu also surfaces the same note inline. See converged design 4 +
    5.5 + section 8 Slice 4.

    Emits:
      - assign-variant(variant): assign this color to the chosen variant.
      - reset-to-auto(): clear this color back to hex auto-classification.
      - close() / update:open(boolean): dismissal (unchanged from Slice 0).
-->
<template>
    <v-menu
        v-model="isOpen"
        location="bottom start"
        :close-on-content-click="false"
        :close-on-back="true"
    >
        <template #activator="{ props: activatorProps }">
            <!--
              The parent supplies the trigger element (the color badge)
              through this pass-through slot, receiving Vuetify's activator
              props to bind onto it. This keeps all badge presentation +
              testids in the parent while v-menu owns open/close behavior.
            -->
            <slot name="activator" :props="activatorProps" />
        </template>

        <div
            data-testid="variant-menu-root"
            class="variant-menu"
            role="dialog"
            aria-label="Variant routing"
        >
            <div
                data-testid="variant-menu-current"
                class="variant-menu-current"
            >
                Currently: {{ currentVariantLabel || "(auto)" }}
            </div>

            <!--
              Slice 4 steal note: when the color currently belongs to a
              DIFFERENT variant, remind the user that assigning it here
              moves it. The pip itself also carries this tooltip (parent).
            -->
            <div
                v-if="currentOwnerLabel"
                data-testid="variant-menu-steal-note"
                class="variant-menu-steal-note"
            >
                On {{ currentOwnerLabel }} -- assigning moves it here.
            </div>

            <div class="variant-menu-options">
                <button
                    v-for="opt in variantOptions"
                    :key="opt.key"
                    type="button"
                    :data-testid="`variant-menu-assign-${opt.key}`"
                    class="variant-menu-button"
                    :class="{ 'is-active': opt.isCurrent }"
                    :disabled="opt.isCurrent"
                    :aria-current="opt.isCurrent ? 'true' : undefined"
                    @click="onAssign(opt.variant)"
                >
                    <span class="variant-menu-button-label">{{ opt.label }}</span>
                    <span
                        v-if="opt.isCurrent"
                        class="variant-menu-button-tag"
                        >current</span
                    >
                </button>
            </div>

            <button
                type="button"
                data-testid="variant-menu-reset"
                class="variant-menu-reset"
                @click="onReset"
            >
                Reset to auto ({{ autoLabel }})
            </button>

            <button
                type="button"
                data-testid="variant-menu-close"
                class="variant-menu-close"
                aria-label="Close"
                @click="onClose"
            >
                x
            </button>
        </div>
    </v-menu>
</template>

<script setup>
import { computed } from "vue";
import { classifyLDWithName } from "@/utils/luminance";
import { variantKindLabel } from "@/utils/variantKindLabels";

const props = defineProps({
    colorSku: { type: String, required: true },
    hex: { type: String, default: null },
    name: { type: String, default: null },
    // The current resolved kind + human label for the color (badge state).
    currentKind: { type: String, default: null },
    currentVariantLabel: { type: String, default: "" },
    // Slice 4: the placement's live variants (each {id, kind, color_skus,
    // name}). Drives the per-variant option list. Empty when the placement
    // has no variants -- then only "Reset to auto" shows.
    variants: { type: Array, default: () => [] },
    // Slice 4: the id of the variant that CURRENTLY owns this color (the
    // resolver's answer), or null. The matching option is disabled +
    // marked current.
    currentVariantId: { type: [Number, String], default: null },
    // Slice 4: human label of the variant that currently OWNS this color
    // when it belongs to a DIFFERENT variant than a hovered target -- drives
    // the steal note. Null when the color is unowned / auto.
    currentOwnerLabel: { type: String, default: null },
    // Slice 0: v-menu open state, two-way bound from the parent
    // (v-model:open).
    open: { type: Boolean, default: false },
    // Product colors ({color_sku, name}) so specific-variant labels can
    // render color names rather than raw SKUs.
    productColors: { type: Array, default: () => [] },
});

const emit = defineEmits([
    "assign-variant",
    "reset-to-auto",
    "close",
    "update:open",
]);

// Slice 0: proxy v-menu's v-model through to the parent's v-model:open.
const isOpen = computed({
    get: () => props.open,
    set: (value) => emit("update:open", value),
});

// Slice 4: one option per placement variant. Label prefers the variant's
// custom name (Slice 1), else the kind-derived routing label. The option
// for the variant that currently owns this color is marked current +
// disabled (nothing to reassign).
const variantOptions = computed(() => {
    const list = Array.isArray(props.variants) ? props.variants : [];
    return list
        .filter((v) => v && (v.id != null || v._tempId != null))
        .map((v) => {
            const id = v.id ?? v._tempId;
            const custom = typeof v.name === "string" ? v.name.trim() : "";
            const roleLabel =
                variantKindLabel(v, { productColors: props.productColors })
                || "Variant";
            const label = custom || roleLabel;
            const isCurrent =
                props.currentVariantId != null
                && String(id) === String(props.currentVariantId);
            return {
                key: String(id),
                variant: v,
                label,
                isCurrent,
            };
        });
});

// Auto classification per the same pipeline the Stage uses. Surfaces the
// "Reset to auto (Light)" / "(Dark)" hint so the distributor sees what auto
// would land on if they cleared their routing.
const autoLabel = computed(() => {
    const { ld } = classifyLDWithName({ hex: props.hex, name: props.name });
    if (ld === "light") return "Light";
    if (ld === "dark") return "Dark";
    return "auto";
});

function onAssign(variant) {
    if (!variant) return;
    emit("assign-variant", variant);
    // Co-emit close so the parent clears its open state in the same tick
    // the routing is applied. With :close-on-content-click="false" the
    // panel would otherwise stay open after the action fired.
    emit("close");
}

function onReset() {
    emit("reset-to-auto");
    emit("close");
}

function onClose() {
    emit("close");
}
</script>

<style scoped>
/*
 * Slice 0: the panel is rendered inside the Vuetify v-menu overlay layer
 * (teleported to body). `position: relative` anchors the absolutely-
 * positioned close button inside the panel.
 */
.variant-menu {
    position: relative;
    background: var(--tf-bg-surface, #ffffff);
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    padding: 12px;
    min-width: 200px;
    max-width: 280px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.variant-menu-current {
    font-size: 12px;
    font-weight: 600;
    color: var(--tf-text-default, #1f2937);
    padding-right: 18px;
}

.variant-menu-steal-note {
    font-size: 11px;
    color: var(--tf-text-muted, #6b7280);
    font-style: italic;
    line-height: 1.3;
}

.variant-menu-options {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.variant-menu-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    width: 100%;
    padding: 6px 8px;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 4px;
    background: var(--tf-bg-surface, #ffffff);
    font-size: 12px;
    text-align: left;
    cursor: pointer;
}

.variant-menu-button:hover:not(:disabled) {
    border-color: var(--tf-brand-primary, #119ae9);
}

.variant-menu-button:disabled {
    cursor: default;
    opacity: 0.8;
}

.variant-menu-button.is-active {
    background: var(--tf-brand-primary, #119ae9);
    color: #ffffff;
    border-color: var(--tf-brand-primary, #119ae9);
}

.variant-menu-button-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.variant-menu-button-tag {
    flex: 0 0 auto;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.variant-menu-reset {
    padding: 6px 8px;
    background: transparent;
    border: none;
    color: var(--tf-brand-primary, #119ae9);
    font-size: 12px;
    text-align: left;
    cursor: pointer;
}

.variant-menu-close {
    position: absolute;
    top: 4px;
    right: 4px;
    background: transparent;
    border: none;
    font-size: 12px;
    color: var(--tf-text-muted, #6b7280);
    cursor: pointer;
    padding: 2px 6px;
}
</style>
